import React from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Button, Fieldset, Form, Spinner } from '@vwfs-bronson/bronson-react'

import merge from 'lodash/merge'
import getInitialValues from './initial-values'
import RenderFieldset from './fieldset'
import getValidationSchema from './validation-schema'
import { normalizeSalutation } from '../utils'
import {
  scrollToActiveFormSection,
} from '../../../services/common/form'
import { getDataModels } from '../../../services/redux/features/data-models.redux'
import { isAudi, scrollElWithDelay } from '../../../services/common/utils'

import {
  getFormData,
  saveFormData,
} from '../../../services/redux/features/form.redux'
import { resetOpenSectionState } from '../../../components/FormSection'

import { Personal } from '../../../models/AppPage/Personal'
import { storePersonalData } from '../../../services/api/forms/personal/personal'
import { setActualPage } from '../../../services/redux/features/custom.redux'
import { getStorefrontData } from '../../../services/redux/features/storefront.redux'
import {
  getKeycloak,
  getKeycloakIdpHint,
  getKeycloakTokens,
  setKeycloak,
} from '../../../services/redux/features/keycloak.redux'
import {
  fillDatalayer,
  pageName,
  trackAbort,
  trackDirty,
  trackErrors,
  trackPage,
  trackSuccess,
} from './tracking'

export type PersonalDetailsProps = {
  onValid: any
  saveFormData: any
  dataModels: any
  formData: any
}

const PersonalDetails = (props, ref) => {
  const {
    updateValidSections,
    dataModels,
    formData,
    storefrontData,
    hidden,
    editPage,
    keycloakData,
    onSubmitParent
  } = props
  let submitFunction
  React.useImperativeHandle(ref, () => ({
    submit: () => {
      submitFunction()
    },
    getData: () => {
      return formState.current.values
    },
    isValid: () => {
      return formState.current.isValid
    },
    validate: () => {
      props.onValid(formState.current.isValid)
      return formState.current.isValid
    },
  }))
  const formState = React.useRef({} as any)
  const [initialValid, setInitialValid] = React.useState<boolean | null>(null)
  const [errors, setErrors] = React.useState(undefined as any)
  const [isSubmitPressed, setIsSubmitPressed] = React.useState(false)
  const [, setTouched] = React.useState()
  const [isloading, setisloading] = React.useState(false)
  const [isDirty, setIsDirty] = React.useState(false)
  const [lastTouchedField, setLastTouchedField] = React.useState(undefined as any)

  const onSubmit = (values: Personal) => {
    onSubmitParent(values)
    const { salutation, licenseCheck, ...salutationCleanValues } = values
    const normalizedSalutation = normalizeSalutation(salutation)
    const newValues = salutationCleanValues.title
      ? salutationCleanValues
      : {
          ...salutationCleanValues,
          title: 'NONE',
        }
    const payload = normalizedSalutation
      ? { salutation: normalizedSalutation, ...newValues }
      : { ...newValues }

    setisloading(true)
    storePersonalData(payload)
      .then(() => {
        trackSuccess()
        setisloading(false)
        resetOpenSectionState()
        updateValidSections('personalDetails', true)
        props.saveFormData(merge(formData, { personalDetails: { ...values, salutation: normalizedSalutation } }))
        fillDatalayer({ salutation: normalizedSalutation, ...newValues })
        scrollElWithDelay(scrollToActiveFormSection)
      })
      .catch(() => {
        setisloading(false)
        props.onErrorFetch()
        trackAbort(lastTouchedField)
      })
  }

  const onChange = ev => {
    updateValidSections('personalDetails', false)
  }

  const onBlur = ev => {
    if (ev && ev.target && ev.target.name) {
      setLastTouchedField(
        ev.target.name.split('.')[ev.target.name.split('.').length - 1]
      )
    }
  }

  React.useEffect(() => {
    if (!hidden) {
      props.setActualPage(pageName)
      // we want to be sure that the page track comes first
      trackPage(editPage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, editPage])

  React.useEffect(() => {
    if (isDirty) {
      trackDirty()
    }
  }, [isDirty])
  React.useEffect(() => {
    window.addEventListener('beforeunload', () => trackAbort(lastTouchedField))
    return () => {
      window.removeEventListener('beforeunload', () =>
        trackAbort(lastTouchedField)
      )
    }
  })

  const { t } = useTranslation()

  const isInitialValid = formikProps => {
    if (initialValid !== null) {
      return initialValid
    }
    if (storefrontData) {
      const isValid = getValidationSchema(storefrontData, t)?.isValidSync(
        formikProps.initialValues
      )
      setInitialValid(isValid)
      return isValid
    }
    return false
  }

  return (
    <>
      {Object.keys(dataModels).length ? (
        <Formik
          enableReinitialize
          isInitialValid={isInitialValid}
          initialValues={getInitialValues({
            formData,
            keycloakData,
            storefrontData,
          })}
          validationSchema={getValidationSchema(storefrontData,t)}
          onSubmit={values => onSubmit(values)}
          render={(formProps: any) => {
            const { values, handleSubmit, setFieldValue } = formProps
            setErrors(formProps.errors)
            if (errors && isSubmitPressed) {
              trackErrors(errors, setIsSubmitPressed)
            }
            setTouched(formProps.touched)
            formState.current = {
              values,
              isValid: formProps.isValid,
              dirty: formProps.dirty,
            }
            setIsDirty(formProps.dirty)
            submitFunction = formProps.submitForm
            const handleSubmitOwn = e => {
              setIsSubmitPressed(true)
              setTimeout(() => setIsSubmitPressed(false), 500)
              handleSubmit(e)
            }

            return (
              <Form
                onChange={onChange}
                onSubmit={handleSubmitOwn}
                onBlur={onBlur}
                floatingLabel
              >
                {RenderFieldset(
                  t,
                  dataModels,
                  values,
                  setFieldValue,
                  storefrontData
                )}
                <Fieldset>
                  <Fieldset.Row className="u-text-right u-mt-small">
                    <Button
                      id="personalDetailsButton"
                      testId="personalDetailsButton"
                      type="submit"
                    >
                      {isloading ? (
                        <Spinner section center small>
                          {isAudi() ? <span>&nbsp;</span> : ''}
                        </Spinner>
                      ) : (
                        t('form:btnContinue')
                      )}
                    </Button>
                  </Fieldset.Row>
                </Fieldset>
              </Form>
            )
          }}
        />
      ) : (
        <div className="u-p-xlarge">
          <Spinner section />
        </div>
      )}
    </>
  )
};

const mapStateToProps = state => {
  return {
    dataModels: getDataModels(state),
    formData: getFormData(state),
    storefrontData: getStorefrontData(state),
    getIdpHint: getKeycloakIdpHint(state),
    getTokens: getKeycloakTokens(state),
    keycloakData: getKeycloak(state),
  }
}

export default connect(
  mapStateToProps,
  { saveFormData, setActualPage, setKeycloak },
  null,
  {
    forwardRef: true,
  }
)(React.forwardRef(PersonalDetails))
